import React, { useState } from "react";
import {
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Alert,
  Switch,
  View,
} from "react-native";
import { RouteProp, useRoute } from "@react-navigation/native";
import { observer } from "mobx-react";
import { EnumRoutesAppWeb } from "../enum/EnumRoutesAppWeb";
import { goBack } from "../services/NavigationService";
import { Theme } from "../enum/Theme";
import PhotoUploader from "./PhotoUploader";
import RichTextEditor from "./RichTextEditor";
import SpaceH from "@comps/SpaceH";
import { Shopping } from "@interfaces/ishopping";
import { shoppingStore } from "../stores/ShoppingStore";
import { Language } from "@constants/language";
import { getFlagByLanguage } from "../services/getFlagByLanguage";
import { ModalFiltroLanguage } from "../comps/ModalFiltroLanguage";
import { LanguageSelector } from "../comps/LanguageSelector";

type PostFormRouteProp = RouteProp<
  { [EnumRoutesAppWeb.createEditShopping]: { item: Shopping } },
  EnumRoutesAppWeb.createEditShopping
>;

const ShoppingForm = observer(() => {
  const route = useRoute<PostFormRouteProp>();
  const post = route.params?.item;

  const [title, setTitle] = useState(post?.title || "");
  const [fulltext, setFulltext] = useState(post?.fulltext || "");
  const [img, setImg] = useState(post?.img || "");
  const [interestFree, setInterestFree] = useState(post?.interestFree || false);
  const [link, setLink] = useState(post?.link || "");
  const [installments, setInstallments] = useState(
    post?.installments?.toString() || "1"
  );
  const [price, setPrice] = useState(
    post?.price?.toFixed(2)?.toString() || "0.00"
  );
  const [priceList, setPriceList] = useState(
    post?.priceList?.toFixed(2)?.toString() || "0.00"
  );
  const [media, setMedia] = useState(post?.medias || []);
  const [mainMediaUrl, setMainMediaUrl] = useState(
    post?.medias?.[post?.imageDefault || 0]?.url || ""
  );
  const [lang, setLang] = useState<Language>(post?.lang || "pt");


  const isFormValid = () => {
    const isTextComplete = title && fulltext && link && price && priceList && lang;
    return isTextComplete;
  };

  const handleSave = async () => {
    if (!isFormValid()) {
      Alert.alert(
        "Atenção",
        "Preencha todos os campos obrigatórios, incluindo o idioma."
      );
      return;
    }

    const itemToSave: Omit<Shopping, "id"> = {
      title,
      fulltext,
      img,
      interestFree,
      link,
      lang,
      imageDefault: media.findIndex((m: any) => m.url === mainMediaUrl),
      installments: parseInt(installments, 10),
      price: parseFloat(price),
      priceList: parseFloat(priceList),
      medias: media,
    };

    await shoppingStore.saveItem(itemToSave, post?.id);
    goBack();
  };

  return (
    <ScrollView style={styles.container}>
      <Text style={styles.title}>{post ? "Editar Item" : "Novo Item"}</Text>


      <Text style={styles.section}>Título:</Text>
      <TextInput
        placeholder="Digite o título do item"
        value={title}
        onChangeText={(v) => setTitle(v)}
        style={styles.input}
      />

      <LanguageSelector value={lang} onChange={setLang} />

      <Text style={styles.section}>Texto Completo:</Text>
      <RichTextEditor
        initialContent={fulltext || ""}
        onChange={(v) => setFulltext(v)}
      />

      <SpaceH />

      <Text style={styles.section}>Link do Produto:</Text>
      <TextInput
        placeholder="https://"
        value={link}
        onChangeText={(v) => setLink(v)}
        style={styles.input}
        autoCapitalize="none"
      />

      <Text style={styles.section}>Imagem Principal (URL):</Text>
      <TextInput
        placeholder="https://"
        value={img}
        onChangeText={(v) => setImg(v)}
        style={styles.input}
        autoCapitalize="none"
      />

      <Text style={styles.section}>Parcelas:</Text>
      <TextInput
        placeholder="Ex: 10"
        keyboardType="number-pad"
        value={installments}
        onChangeText={(v) => setInstallments(v)}
        style={styles.input}
      />

      <Text style={styles.section}>Preço:</Text>
      <TextInput
        placeholder="Ex: 199.99"
        keyboardType="decimal-pad"
        value={price}
        onChangeText={(v) => setPrice(v)}
        style={styles.input}
      />

      <Text style={styles.section}>Preço de Lista:</Text>
      <TextInput
        placeholder="Ex: 249.99"
        keyboardType="decimal-pad"
        value={priceList}
        onChangeText={(v) => setPriceList(v)}
        style={styles.input}
      />

      <Text style={styles.section}>Sem Juros:</Text>
      <Switch
        value={interestFree}
        onValueChange={setInterestFree}
        style={styles.switchContainer}
      />

      <SpaceH />

      <PhotoUploader
        showAudio={false}
        showVideo={false}
        postId={post?.id || "temp"}
        media={media}
        setMedia={setMedia}
        mainMediaUrl={mainMediaUrl}
        setMainMediaUrl={setMainMediaUrl}
      />

      <TouchableOpacity
        style={[
          styles.saveButton,
          { backgroundColor: isFormValid() ? Theme.foreground : "#ccc" },
        ]}
        onPress={handleSave}
        disabled={!isFormValid()}
      >
        <Text style={styles.saveButtonText}>Salvar</Text>
      </TouchableOpacity>

    
    </ScrollView>
  );
});

export default ShoppingForm;

const styles = StyleSheet.create({
  container: { flex: 1, padding: 20, backgroundColor: "#F2F2F2" },
  title: { fontSize: 22, fontWeight: "bold", marginBottom: 20 },
  section: { marginTop: 15, fontWeight: "bold" },
  input: {
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 5,
    padding: 10,
    marginBottom: 10,
  },
  switchContainer: {
    marginVertical: 10,
  },
  saveButton: {
    padding: 15,
    borderRadius: 5,
    marginTop: 20,
    backgroundColor: Theme.foreground,
  },
  saveButtonText: { color: "#fff", textAlign: "center", fontWeight: "bold" },
  langBox: {
    alignSelf: "flex-start",
    backgroundColor: "#e6e6e6",
    padding: 8,
    borderRadius: 5,
    marginBottom: 10,
  },
  langText: {
    fontSize: 14,
    fontWeight: "bold",
  },
});
